import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { withRouter } from "react-router";
import {
  Container,
  Row, 
  Col, ColBr,
  Img,
  Link
} from './StyledComponents.js'
import styled from 'styled-components'
import {Helmet} from "react-helmet-async";
import moment from 'moment'
import { useIntl, FormattedMessage } from "react-intl"
import ReactMarkdown from 'react-markdown'

import useSsr from './useSsr'
import useLang from './useLang'
import altTextKes from './utils/altTextKes';
import AmpSafeImg from './AmpSafeImg.js';
import PaginationUstuAd from './components/ads/PaginationUstu'
import UstBannerAd from './components/ads/UstBannerAd'
import MatchedContentAd from './components/ads/MatchedContentAd'
import Pagination from './components/Pagination'
import CommonPagination from './CommonPagination';
import useTenant from './client/useTenant'
import {useCsEvent} from './client/useEvents'
import GazeteDatePicker from './components/GazeteDatePicker'

const SayfaImg = styled(AmpSafeImg)`
  max-height: 400px;
  margin-top: auto;
`
const SayfaLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height:450px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  transition: box-shadow 0.2s, transform 0.2s;
  overflow: hidden;
  position: relative;
  &:hover, &:focus {
    box-shadow: 0 6px 24px rgba(0,0,0,0.16);
    transform: scale(1.03);
    z-index: 2;
  }
  & img {
    object-fit:contain;
    border-radius: 12px 12px 0 0;
    transition: transform 0.2s;
  }
  &:hover img, &:focus img {
    transform: scale(1.05);
  }
`

// Skeleton loader for cards
const CardSkeleton = styled.div`
  width: 300px;
  height: 450px;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  border-radius: 16px;
  margin: 0.5rem;
  animation: skeleton-loading 1.2s infinite linear;
  @keyframes skeleton-loading {
    0% { background-position: -300px 0; }
    100% { background-position: 300px 0; }
  }
`

const Title = styled.h1`
  margin: 0.2rem 0rem;
  font-size: 1.2rem;
`
const Aciklama = styled(Row)`
  background-color: beige;
`
export const ITEM_COUNT = 60;

const paginationUrlGenerator = (page, gazete) => {
  return (page !== 1) ? `/gazete/${gazete}/${page}` : `/gazete/${gazete}` 
} 

const gqlQuery = gql`
  query Gazete($gazete: String!, $count: Int, $offset: Int) {
    gazete(gazete: $gazete) {
      gazete, 
      gorunen_ad,
      type,
      logo,
      dil,
      aciklama,
      sayilar(count: $count, offset: $offset) {
        tarih,
        thumbnail,
        alt_text,
        caption
      },
      yillar,
      toplamSayi
    }
  }
`

const BackToTopButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  background: #8b7d65;
  color: white;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  font-size: 2rem;
  transition: background 0.2s;
  &:hover, &:focus {
    background: #a08e6c;
    outline: 2px solid #fffbe6;
  }
`;

function GazeteComponent(props) {
  const intl = useIntl()
  const lang = useLang()
  const {isSsr, context: ssrContext} = useSsr()
  const tenant = useTenant()
  const fireCsEvent = useCsEvent()

  let gazete = props.match.params.gazete
  let page = parseInt(props.match.params.page || 1)

  let { loading, data} = useQuery(
    gqlQuery, { 
      variables: {
        gazete, 
        count: ITEM_COUNT, 
        offset: (page-1)*ITEM_COUNT
      }
    }
  );  
 
  const {toplamSayi, sayilar, yillar, type} = data?.gazete || {};

  const renderTip = React.useCallback(() => {
    return lang.lang === 'tr'
      ? (type === 'gazete' ? "Gazetesi" : "Dergisi")
      : (type === 'gazete' ? "Newspaper" : "Journal")
  }, [type, lang.lang])

  const renderArasiSayilar = React.useCallback(() => {
    if (page <= 1) return "";
    if (!sayilar?.length) return "";

    const msg = intl.formatMessage(
      {id: "Gazete.arasıSayılar", defaultMessage: "{ilk} - {son} arası sayılar."}, 
      {
        ilk: moment(sayilar[0].tarih).format('LL'), 
        son: moment(sayilar[sayilar.length-1].tarih).format('LL')
      }
    )
    return ` ${msg}`
  }, [intl, page, sayilar])
  
  if (isSsr && !loading && !data?.gazete ) {
    ssrContext.notFound = true
  }

  console.debug("Gazete comp render"
    , "lang:", lang
    , "loading:", loading
    , "gazete data:", !!data?.gazete
  )

  // Back to top button state
  const [showBackToTop, setShowBackToTop] = React.useState(false);
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container>
    {!loading && data?.gazete && <React.Fragment>
      <Helmet>
        {(page-1) >= 1 && <link rel="prev" href={`${process.env.REACT_APP_GA_BASE_URL}${paginationUrlGenerator(page-1, gazete)}`} />}
        {(page+1) < toplamSayi/ITEM_COUNT && <link rel="next" href={`${process.env.REACT_APP_GA_BASE_URL}${paginationUrlGenerator(page+1, gazete)}`} />}
        {<link rel="canonical" href={`${process.env.REACT_APP_GA_BASE_URL}${paginationUrlGenerator(page, gazete)}`} />}
        <title>{`${intl.formatMessage({id: "Gazete.title"}, {ad: data.gazete?.gorunen_ad, tip: renderTip()})} ${page>1 ? `${moment(sayilar[0]?.tarih)?.format('LL')} - ${moment(sayilar[sayilar.length-1]?.tarih)?.format('LL')} ` : ""} | ${intl.formatMessage({id: "name"})}`}</title>
        <meta name="description" 
          content={`${intl.formatMessage({id: "Gazete.description"}, {ad: data.gazete?.gorunen_ad, tip: renderTip()})}${renderArasiSayilar()}`} />
        <meta property="og:title" content={`${data.gazete?.gorunen_ad} Arşivi`} /> 
        <meta name="og:description" content={`${intl.formatMessage({id: "Gazete.description"}, {ad: data.gazete?.gorunen_ad, tip: renderTip()})} ${renderArasiSayilar()}`} />
        <meta name="og:image" content={`${process.env.REACT_APP_GA_CDN_URL}/${data.gazete?.logo}`} />
        <meta name="twitter:card" content={`summary_large_image`} />
        <meta name="twitter:image" content={`${process.env.REACT_APP_GA_CDN_URL}/${data.gazete?.logo}`} />
        <meta name="twitter:description" content={`${data.gazete?.gorunen_ad} ${renderTip()}'ne ait eski sayıları arşivden okuyun. ${intl.formatDate(new Date(sayilar?.[0]?.tarih))} - ${intl.formatDate(new Date(sayilar?.[sayilar?.length-1]?.tarih))} arası sayılar.`} />
        <meta property="twitter:title" content={`${data.gazete?.gorunen_ad} Arşivi`} />
      </Helmet>
      <Row><Col>
        <Title className='d-flex'>
          <FormattedMessage id="Gazete.title" 
            values={{ad: data.gazete?.gorunen_ad, tip: renderTip()}} />
        </Title>
      </Col></Row>
      {page == 1 && lang.lang == data?.gazete?.dil && 
          data?.gazete?.aciklama && <React.Fragment>
          <Row><Col>
            <h2><FormattedMessage id="Gazete.briefInfo" /></h2>
          </Col></Row>
            <Row style={{backgroundColor: 'beige'}} className='mb-1'><Col>
              <ReactMarkdown>{data?.gazete?.aciklama}</ReactMarkdown>
            </Col></Row>
        </React.Fragment>}
      <Row>
        <Col className="mt-1 mb-1 d-flex">
          <p style={{fontSize: '1rem'}}>
            <FormattedMessage
              id="Gazete.archiveInfo"
              defaultMessage="Arşivde {ilkYil} ve {sonYil} yılları arasında {toplamSayi} sayı bulunmaktadır. Takvimden sayı olan bir günü seçebilir veya tüm sayılara göz atabilirsiniz."
              values={{
                ilkYil: yillar[0],
                sonYil: yillar[yillar.length-1],
                toplamSayi
              }}
            />
          </p>
        </Col>
      </Row>
      <Row className='mb-2 d-flex'><Col className='d-flex'>
        <UstBannerAd />
      </Col></Row>
      <Row>
        <Col className='d-flex'>
          <GazeteDatePicker gazete={gazete} />
        </Col>
      </Row>
      <Row><Col>
        <h2>
          {page > 1 ? (
            <FormattedMessage id="Gazete.allIssuesPage" values={{ page }} />
          ) : (
            <FormattedMessage id="Gazete.allIssues" />
          )}
        </h2>
      </Col></Row>
      <Row>
        {sayilar?.map((sayi, index) => {
          const tarih = moment(sayi.tarih).format('LL');
          const issueUrl = `/gazete/${gazete}/${moment(sayi.tarih).format('YYYY-MM-DD')}/1`;
          return (
            <React.Fragment key={["sayi", sayi.tarih]} >
            <ColBr 
              itemScope itemType="https://schema.org/Article"
              className="mb-1 d-flex justify-content-center" >
              <SayfaLink 
                itemProp="mainEntityOfPage"
                to={issueUrl}
                onClick={() => {fireCsEvent({
                  category: "Gazete",
                  action: "Navigation",
                  label: "Sayı Link"
                })}}
                title={`${intl.formatDate(new Date(sayi.tarih))} sayısı`} aria-label={`${intl.formatDate(new Date(sayi.tarih))} sayısı`}
              >
                <h3 className='text-center mb-1 mt-1'>
                  {intl.formatDate(new Date(sayi.tarih), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
                <SayfaImg
                    ampProps={{width: 300, height:450}}
                    src={`${process.env.REACT_APP_GA_CDN_URL}/${sayi.thumbnail}`} 
                    alt={sayi.caption || altTextKes(sayi.alt_text, 125) || `${data.gazete?.gorunen_ad} ${renderTip()} ${intl.formatDate(new Date(sayi.tarih))} kapağı`} 
                    loading="lazy" width="300" height="450">
                  <Img/>
                </SayfaImg>
              </SayfaLink>
            </ColBr>
            </React.Fragment>
          );
        })}
      </Row>
      <PaginationUstuAd />
      <Row><Col>
        <CommonPagination
          page={page}
          total={toplamSayi}
          pageSize={ITEM_COUNT}
          query={gazete}
          location={props.location}
          fireCsEvent={fireCsEvent}
          ariaLabel={intl.formatMessage({id: "Gazete.navigation"})}
          urlGenerator={(targetPage, gazete) => targetPage === 1 ? `/gazete/${gazete}` : `/gazete/${gazete}/${targetPage}`}
          eventCategory="Gazete"
        />
      </Col></Row>
      <Row><MatchedContentAd slotId="9580851941" /></Row>
      {/* Back to Top Button */}
      {showBackToTop && (
        <BackToTopButton onClick={scrollToTop} aria-label="Başa dön" title="Başa dön">
          ↑
        </BackToTopButton>
      )}
    </React.Fragment> || (
      <Row>
        {[...Array(6)].map((_, i) => (
          <ColBr key={i} className="mb-1 d-flex justify-content-center">
            <CardSkeleton />
          </ColBr>
        ))}
      </Row>
    )} 
    </Container>
  )
}

export default withRouter(GazeteComponent);
